import { useState } from 'react';
import { Shuffle, CalendarDays, Download } from 'lucide-react';
import { Card, CardHeader, CardTitle, StatCard } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { TabBar } from '@/components/ui/TabBar';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { toCsv, downloadCsv } from '@/lib/csv';
import { useSubstitutionReport } from './useSubstitutionReport';
import type { SubstitutionReportType } from './types';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const dateTime = (v: string) => new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const PRESETS: Array<{ id: string; label: string; from: () => string; to: () => string }> = [
  { id: 'today', label: 'Today', from: () => iso(new Date()), to: () => iso(new Date()) },
  { id: '7d', label: '7 days', from: () => daysAgo(6), to: () => iso(new Date()) },
  { id: '30d', label: '30 days', from: () => daysAgo(29), to: () => iso(new Date()) },
];

const STATUS_BADGE: Record<string, 'success' | 'warn' | 'danger' | 'neutral'> = {
  approved: 'success', pending: 'warn', rejected: 'danger', not_required: 'neutral',
};

/**
 * The two report categories the drug-substitution governance feature
 * promised: same-drug/different-strength dose mismatches (report only,
 * never gated) and genuinely-different-drug mismatches (the one that goes
 * through doctor approval — see SubstitutionApprovalsDialog.tsx (opened from the topbar bell) for the live
 * queue this reports on retrospectively). One panel with a type toggle
 * rather than two near-identical files — the table shape barely differs,
 * only whether the approval-status column means anything.
 */
export default function PharmacySubstitutionPanel() {
  const [type, setType] = useState<SubstitutionReportType>('drug');
  const [from, setFrom] = useState(iso(new Date()));
  const [to, setTo] = useState(iso(new Date()));

  const query = useSubstitutionReport(type, from, to);
  const report = query.data;
  const activePreset = PRESETS.find((p) => p.from() === from && p.to() === to)?.id ?? null;

  const exportCsv = () => {
    if (!report) return;
    const csv = toCsv(report.rows, [
      ['created_at', 'Date'],
      ['patient_name', 'Patient'],
      ['patient_uhid', 'UHID'],
      ['prescribed_drug_name', 'Prescribed'],
      ['prescribed_strength', 'Prescribed strength'],
      ['dispensed_drug_name', 'Dispensed'],
      ['dispensed_strength', 'Dispensed strength'],
      ['reason', 'Reason'],
      ['status', 'Status'],
      ['approved_by', 'Decided by'],
      ['bill_number', 'Bill'],
    ]);
    downloadCsv(`pharmacy-${type}-mismatches_${from}_to_${to}.csv`, csv);
  };

  return (
    <section className="mt-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold">
            <Shuffle size={17} className="text-teal" /> Drug substitutions
          </h2>
          <p className="mt-0.5 text-[12px] text-ink-muted">
            Every dispensed line that didn't cleanly match what was prescribed — same drug at a
            different strength, or a genuinely different medicine.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <TabBar
            tabs={[{ id: 'drug', label: 'Drug mismatches' }, { id: 'dose', label: 'Dose mismatches' }]}
            active={type}
            onChange={(id) => setType(id as SubstitutionReportType)}
            size="sm"
          />
          <div className="inline-flex rounded-lg border border-border p-0.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setFrom(p.from()); setTo(p.to()); }}
                className={cn(
                  'rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
                  activePreset === p.id ? 'bg-teal text-white' : 'text-ink-muted hover:bg-muted'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <FormField label="From">
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input type="date" className="w-auto pl-8" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </div>
          </FormField>
          <FormField label="To">
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input type="date" className="w-auto pl-8" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
          </FormField>
          <Button variant="ghost" size="sm" disabled={!report?.rows.length} onClick={exportCsv}>
            <Download size={13} /> Export CSV
          </Button>
        </div>
      </div>

      {query.isError ? (
        <ErrorState message={query.error instanceof ApiError ? query.error.message : 'Could not load the report'} />
      ) : query.isLoading || !report ? (
        <LoadingState label="Loading…" />
      ) : (
        <div className="space-y-4">
          <div className={cn('grid gap-4', type === 'drug' ? 'sm:grid-cols-4' : 'sm:grid-cols-1 max-w-[220px]')}>
            <StatCard accentSide="left" accentColor="var(--teal)">
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Total</div>
              <div className="mt-1.5 text-[26px] font-bold leading-none">{report.summary.total}</div>
            </StatCard>
            {type === 'drug' && (
              <>
                <StatCard accentSide="left" accentColor="var(--color-success)">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Approved</div>
                  <div className="mt-1.5 text-[26px] font-bold leading-none">{report.summary.approved ?? 0}</div>
                </StatCard>
                <StatCard accentSide="left" accentColor="var(--color-warn-ink)">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Pending</div>
                  <div className="mt-1.5 text-[26px] font-bold leading-none">{report.summary.pending ?? 0}</div>
                </StatCard>
                <StatCard accentSide="left" accentColor="var(--color-danger-ink, #B91C1C)">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Rejected</div>
                  <div className="mt-1.5 text-[26px] font-bold leading-none">{report.summary.rejected ?? 0}</div>
                </StatCard>
              </>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle><Shuffle size={15} /> {type === 'drug' ? 'Drug mismatches' : 'Dose mismatches'}</CardTitle>
              <span className="text-[11.5px] text-ink-muted">{report.rows.length} row{report.rows.length === 1 ? '' : 's'}</span>
            </CardHeader>
            {report.rows.length === 0 ? (
              <EmptyState label="Nothing in this range." />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Prescribed</TableHead>
                      <TableHead>Dispensed</TableHead>
                      <TableHead>Reason</TableHead>
                      {type === 'drug' && <TableHead>Status</TableHead>}
                      <TableHead>Bill</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-[12px]">{dateTime(r.created_at)}</TableCell>
                        <TableCell className="text-[12.5px]">
                          {r.patient_name} <span className="text-ink-muted">({r.patient_uhid})</span>
                        </TableCell>
                        <TableCell className="text-[12.5px]">
                          {r.prescribed_drug_name}{r.prescribed_strength ? ` ${r.prescribed_strength}` : ''}
                        </TableCell>
                        <TableCell className="text-[12.5px]">
                          {r.dispensed_drug_name}{r.dispensed_strength ? ` ${r.dispensed_strength}` : ''}
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate text-[12px] text-ink-muted" title={r.reason ?? undefined}>
                          {r.reason || '—'}
                        </TableCell>
                        {type === 'drug' && (
                          <TableCell>
                            <Badge variant={STATUS_BADGE[r.status] ?? 'neutral'}>{r.status.replace('_', ' ')}</Badge>
                            {r.status === 'approved' && r.approved_by && (
                              <div className="mt-0.5 text-[10.5px] text-ink-muted">by {r.approved_by}</div>
                            )}
                            {r.status === 'rejected' && r.rejection_reason && (
                              <div className="mt-0.5 text-[10.5px] text-ink-muted" title={r.rejection_reason}>{r.rejection_reason}</div>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-[12px]">{r.bill_number || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      )}
    </section>
  );
}
